/**
 * The ISO / hub / certificate registry.
 *
 * Codes, Platts curve locations and LMP node names are taken verbatim from
 * `pilot-data/src/migrations/20260826154411_market_topology_baseline.sql` so
 * that swapping the mock source for the real API needs no re-mapping. Hubs the
 * migration lists as unmapped (PJM West, Mass Hub, Indiana Hub, both SPP hubs)
 * carry `inMigration: false`. Gas points come from `market.gas_hub_labels`.
 *
 * Data only — no colors, no JSX. The design-token gate greps every `.ts` file
 * for hex literals, including comments.
 */

import type {
  IsoCode,
  MarketHub,
  MarketIso,
  MarketRegion,
  RecCertificate,
} from "@/types/market-intelligence";

export const MARKET_ISOS: readonly MarketIso[] = [
  { code: "CAISO", name: "California ISO", states: ["CA"] },
  {
    code: "ERCOT",
    name: "Electric Reliability Council of Texas",
    states: ["TX"],
  },
  {
    code: "ISO-NE",
    name: "ISO New England",
    states: ["CT", "MA", "ME", "NH", "RI", "VT"],
  },
  {
    code: "MISO",
    name: "Midcontinent ISO",
    // Its fed points' states, as the server derives them (AB#8604), so the
    // chips don't flash MISO's whole footprint before the topology lands.
    states: ["IL", "MI"],
  },
  { code: "NYISO", name: "New York ISO", states: ["NY"] },
  {
    code: "PJM",
    name: "PJM Interconnection",
    states: ["DC", "DE", "IL", "IN", "KY", "MD", "MI", "NC", "NJ", "OH", "PA", "TN", "VA", "WV"],
  },
  {
    code: "SPP",
    name: "Southwest Power Pool",
    states: ["AR", "IA", "KS", "LA", "MN", "MO", "MT", "ND", "NE", "NM", "OK", "SD", "WY"],
  },
] as const;

type HubSeed = Omit<MarketHub, "commodity"> & { commodity: "power" };

const POWER_HUBS: HubSeed[] = [
  // ── PJM ────────────────────────────────────────────────────────────────
  h("pjm_west", "West", "PJM", "hub", "PJM West Financial", "WESTERN HUB", null, true, false, false),
  h("pjm_comed", "ComEd", "PJM", "load_zone", "PJM ComEd Financial", "COMED", "pjm_west", false, true, true),
  h("pjm_atsi", "ATSI", "PJM", "load_zone", "PJM ATSI Financial", "ATSI", "pjm_west", false, true, true),
  h("pjm_bge", "BGE", "PJM", "load_zone", "PJM BGE Zone Financial", "BGE", "pjm_west", false, true, true),
  h("pjm_pseg", "PSEG", "PJM", "load_zone", "PJM PSEG Zone Financial", "PSEG", "pjm_west", false, true, true),
  h("pjm_dom", "Dominion", "PJM", "load_zone", "PJM Dominion Hub Financial", "DOM", "pjm_west", false, true, true),
  // ── CAISO ──────────────────────────────────────────────────────────────
  h("caiso_sp15", "SP15", "CAISO", "hub", "South Path 15 Financial", "TH_SP15_GEN-APND", null, true, false, true),
  h("caiso_np15", "NP15", "CAISO", "hub", "North Path 15 Financial", "TH_NP15_GEN-APND", null, false, false, true),
  h("caiso_dlap_sce", "SCE", "CAISO", "load_zone", null, "DLAP_SCE-APND", "caiso_sp15", false, false, true),
  // ── ERCOT ──────────────────────────────────────────────────────────────
  h("ercot_hb_north", "North", "ERCOT", "hub", "ERCOT North Financial", "HB_NORTH", null, true, false, true),
  h("ercot_hb_houston", "Houston", "ERCOT", "hub", "ERCOT Houston Financial", "HB_HOUSTON", null, false, false, true),
  h("ercot_hb_west", "West", "ERCOT", "hub", "ERCOT West Financial", "HB_WEST", null, false, false, true),
  // ── NYISO ──────────────────────────────────────────────────────────────
  h("nyiso_g", "Zone G", "NYISO", "load_zone", "East NY ZnG Financial", "HUD VL", null, true, false, true),
  h("nyiso_j", "Zone J", "NYISO", "load_zone", "East NY ZnJ Financial", "N.Y.C.", "nyiso_g", false, false, true),
  h("nyiso_a", "Zone A", "NYISO", "load_zone", "West NY ZnA Financial", "WEST", "nyiso_g", false, false, true),
  // ── ISO-NE ─────────────────────────────────────────────────────────────
  h("isone_mass_hub", "Mass Hub", "ISO-NE", "hub", "ISO-NE Mass Hub Financial", ".H.INTERNAL_HUB", null, true, false, false),
  h("isone_nema", "NEMA", "ISO-NE", "load_zone", "ISO-NE NE-Mass Financial", ".Z.NEMASSBOST", "isone_mass_hub", false, false, true),
  h("isone_ct", "Connecticut", "ISO-NE", "load_zone", null, ".Z.CONNECTICUT", "isone_mass_hub", false, false, true),
  // ── MISO ───────────────────────────────────────────────────────────────
  h("miso_hub_indiana", "Indiana", "MISO", "hub", "MISO Indiana Hub Financial", "INDIANA.HUB", null, true, false, false),
  h("miso_hub_illinois", "Illinois", "MISO", "hub", "MISO Illinois Hub Financial", "ILLINOIS.HUB", null, false, false, true),
  h("miso_hub_michigan", "Michigan", "MISO", "hub", "Michigan Financial", "MICHIGAN.HUB", null, false, false, true),
  // ── SPP ────────────────────────────────────────────────────────────────
  h("spp_south", "South", "SPP", "hub", "SPP South Hub Financial", "SPPSOUTH_HUB", null, true, false, false),
  h("spp_north", "North", "SPP", "hub", "SPP North Hub Financial", "SPPNORTH_HUB", null, false, false, false),
];

function h(
  code: string,
  name: string,
  iso: string,
  kind: MarketHub["kind"],
  curveLocation: string | null,
  indexNode: string | null,
  parentHub: string | null,
  headline: boolean,
  hasCapacity: boolean,
  inMigration: boolean,
): HubSeed {
  return {
    code, name, iso, kind, curveLocation, indexNode, parentHub,
    headline, hasCapacity, inMigration, commodity: "power",
  };
}

/**
 * Gas points, as `market.gas_hub_labels` labels them
 * (`20261002181416_market_gas_platts_regions.sql`): the fourteen the lake
 * publishes. The first paint and the seeded generator only — the server's
 * topology is the authority.
 *
 * Each has a Platts region (gas's counterpart to an ISO), a kind — a supply
 * `hub` or a market-area `delivery_point`, gas's counterparts to a hub and a
 * load zone — and Henry Hub as its basis reference, the convention every gas
 * basis is quoted in. `curveLocation` is set where a forward curve quotes the
 * point and `indexNode` where the daily index settles it; Tennessee Zone 6
 * South only settles, as a power settlement-only point does.
 */
const GAS_HUBS: MarketHub[] = [
  g("eastern_gas_north", "Eastern Gas North", "Appalachia", "hub", "Eastern Gas North Financial", true),
  g("henry_hub", "Henry Hub", "Louisiana/Southeast", "hub", "Henry Hub Financial", true),
  g("algonquin_cg", "Algonquin CG", "Northeast", "delivery_point", "Algonquin CG Financial", true),
  g("iroquois_z2", "Iroquois Zone 2", "Northeast", "delivery_point", "Iroquois Zn2 Financial", false),
  g("tenn_z6", "Tennessee Zone 6", "Northeast", "delivery_point", "Tenn Zn6 Dlvd Financial", false),
  g("tenn_z6_south", "Tennessee Zone 6 South", "Northeast", "delivery_point", null, false, "Tennessee Zone 6 delivered South"),
  g("tetco_m3", "TETCO M-3", "Northeast", "delivery_point", "Texas Eastern M-3 Financial", false),
  g("transco_z5", "Transco Zone 5", "Northeast", "delivery_point", "Transco Zn5 Dlvd Financial", false),
  g("transco_z6_ny", "Transco Z6 NY", "Northeast", "delivery_point", "Transco Zn6 NY Financial", false),
  g("transco_z6_nny", "Transco Z6 non-NY", "Northeast", "delivery_point", "Transco Zn6 xNY Financial", false),
  g("pge_cg", "PG&E Citygate", "Southwest", "delivery_point", "PG&E CG Financial", false),
  g("socal_cg", "SoCal Citygate", "Southwest", "delivery_point", "SoCal Gas city-gate Hub Financial", true),
  g("socal_gas", "SoCal Gas", "Southwest", "delivery_point", "SoCal Gas Financial", false),
  g("chicago_cg", "Chicago CG", "Upper Midwest", "delivery_point", "Chicago CG Financial", true),
];

/**
 * One gas point. The daily index settles at the same location the curve is
 * named for, so `indexNode` defaults to the curve location; a settlement-only
 * point passes no curve and names its index location instead.
 */
function g(
  code: string,
  name: string,
  region: string,
  kind: "hub" | "delivery_point",
  curveLocation: string | null,
  headline: boolean,
  indexLocation: string | null = curveLocation,
): MarketHub {
  return {
    code, name, iso: region, commodity: "gas", kind,
    curveLocation, indexNode: indexLocation,
    parentHub: code === "henry_hub" ? null : "henry_hub",
    headline, hasCapacity: false, inMigration: true,
  };
}

/**
 * The gas regions, A–Z, with the states their points serve — the first-paint
 * counterpart of the topology's `gas_regions`, as `MARKET_ISOS` is of `isos`.
 */
export const GAS_REGIONS: readonly MarketRegion[] = [
  { code: "Appalachia", name: "Appalachia", states: ["PA"] },
  { code: "Louisiana/Southeast", name: "Louisiana/Southeast", states: ["LA"] },
  { code: "Northeast", name: "Northeast", states: ["CT", "MA", "NC", "NH", "NJ", "NY", "PA", "RI", "VA"] },
  { code: "Southwest", name: "Southwest", states: ["CA"] },
  { code: "Upper Midwest", name: "Upper Midwest", states: ["IL"] },
];

export const MARKET_HUBS: readonly MarketHub[] = [...POWER_HUBS, ...GAS_HUBS];

const BY_CODE = new Map(MARKET_HUBS.map((hub) => [hub.code, hub]));

export function hubByCode(code: string): MarketHub | undefined {
  return BY_CODE.get(code);
}

export function isoByCode(code: string): MarketIso | undefined {
  return MARKET_ISOS.find((iso) => iso.code === code);
}

/** ISO code from a url slug ("iso-ne" → "ISO-NE"). */
export function isoFromSlug(slug: string): IsoCode | undefined {
  return MARKET_ISOS.find((iso) => iso.code.toLowerCase() === slug.toLowerCase())?.code;
}

export function hubsForIso(iso: string): MarketHub[] {
  return MARKET_HUBS.filter((hub) => hub.iso === iso);
}

export function gasHubs(): MarketHub[] {
  return MARKET_HUBS.filter((hub) => hub.commodity === "gas");
}

/**
 * Whether a point publishes a settled index: what its Index tab, its card's
 * Index chip and a Basis against it all need.
 *
 * The same question for both commodities. Power settles at an ISO node; gas
 * settles through a different feed (`market.settled_gas_index`), and the
 * server sets a gas point's `indexNode` to the location that feed is keyed on
 * — so a gas point with no daily settlement (or a power point with no node)
 * reads as quoted-only. Gas used to key this on its curve location, which
 * could not tell a settlement-only gas point from one that does not settle.
 */
export function pointSettles(hub: Pick<MarketHub, "indexNode">): boolean {
  return hub.indexNode !== null;
}

/** A gas region by its code ("Gulf Coast"), from the static fallback. */
export function gasRegionByCode(code: string): MarketRegion | undefined {
  return GAS_REGIONS.find((r) => r.code === code);
}

/** The hub an ISO is represented by on the Overview. */
export function headlineHubFor(iso: string): MarketHub | undefined {
  const hubs = hubsForIso(iso);
  return hubs.find((hub) => hub.headline) ?? hubs[0];
}

/**
 * Sample certificates for the seeded GENERATOR only.
 *
 * Not the nav's authority and not a name anyone sees on real data — the
 * server sends `display_name` from `market.lake_rec_symbols`, which a
 * super-admin maintains. These names are the ones this file invented before
 * that column existed, and they disagree with the desk's vocabulary
 * (`PJM MD Tier 1` against `Maryland REC Tier 1`); they survive only because
 * `mock-source.ts` needs *something* to draw with no server.
 *
 * **Use `REC_ISOS` to ask whether a region has RECs.** Reading this list for
 * that is what let the nav fall behind the seed — see its note.
 */
export const REC_CERTIFICATES: ReadonlyArray<
  Pick<RecCertificate, "id" | "name" | "iso">
> = [
  { id: "RECPAT1", name: "PJM PA Tier 1", iso: "PJM" },
  { id: "RECNJT1", name: "PJM NJ Class I", iso: "PJM" },
  { id: "RECMDT1", name: "PJM MD Tier 1", iso: "PJM" },
  { id: "RECOHI0", name: "PJM OH", iso: "PJM" },
  { id: "RECCTC1", name: "ISO-NE CT Class I", iso: "ISO-NE" },
  { id: "RECMAC1", name: "ISO-NE MA Class I", iso: "ISO-NE" },
  { id: "RECCAB1", name: "CA Bundled Bucket 1", iso: "CAISO" },
] as const;

/**
 * The markets with a REC compliance program we carry.
 *
 * Mirrors the distinct `iso_code` of the NAMED rows in
 * `market.lake_rec_symbols`, exactly as `CAPACITY_ISOS` below mirrors its
 * Rust counterpart, and kept in step by a test in `pilot-domain` rather than
 * by a fetch — `regionHasSurface` is synchronous and runs while the sidebar
 * renders, so it cannot ask the server.
 *
 * **This replaced a `REC_CERTIFICATES.some(...)` gate, which had gone stale.**
 * That constant is the generator's sample list; it names three ISOs, while
 * the seed now names four. NYISO gained a real certificate and its nav row
 * did not appear, because the nav was reading a fixture. Asking "which
 * regions have RECs" of a list of sample NAMES was the mistake — the nav
 * needs the markets, and nothing else.
 */
export const REC_ISOS: readonly string[] = ["CAISO", "ISO-NE", "NYISO", "PJM"];

/** Whether `iso` has a REC program we carry. Case-insensitive. */
export function isoHasRecs(iso: string): boolean {
  return REC_ISOS.some((c) => c.toLowerCase() === iso.toLowerCase());
}

/**
 * The markets that clear a centralised capacity auction we carry.
 *
 * Mirrors `CAPACITY_ISOS` in `pilot-mod-market-intelligence/src/domain/capacity.rs`, which
 * gates the endpoint. The nav cannot read the server's list — `regionHasSurface`
 * is synchronous and runs while the sidebar renders — so the two are kept in
 * step by the test below rather than by a fetch. A market added on one side
 * and not the other either advertises a row that 404s, or hides a surface the
 * server is happily serving.
 *
 * It is a LIST, not `iso === "PJM"`, because capacity is not a PJM property:
 * MISO runs a PRA and ISO-NE an FCM, and each arrives by adding a code here
 * and in the Rust constant. What each market does NOT share is the shape —
 * unit and period grain travel on the wire for exactly that reason.
 */
export const CAPACITY_ISOS: readonly string[] = ["PJM"];

/** Whether `iso` clears a capacity auction we carry. */
export function isoHasCapacity(iso: string): boolean {
  return CAPACITY_ISOS.some((code) => code.toLowerCase() === iso.toLowerCase());
}
